<template>
  <section v-if="isLoading" class="mx-auto grid min-h-[60vh] max-w-2xl place-items-center px-4 text-center">
    <div>
      <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" aria-hidden="true"></div>
      <p class="mt-4 text-sm font-medium text-slate-600">Loading flyer images...</p>
    </div>
  </section>

  <section v-else-if="loadError || !apiFlyer" class="mx-auto grid min-h-[60vh] max-w-2xl place-items-center px-4 text-center">
    <div>
      <p class="text-base font-semibold text-slate-900">{{ loadError || 'Flyer details could not be loaded.' }}</p>
      <button class="nf-primary-button mt-4" type="button" @click="fetchFlyerDetail">
        Try again
      </button>
    </div>
  </section>

  <CreateFlyer
    v-else
    :initial-flyer="apiFlyer"
    :page-title="editPageTitle"
    submit-label="Update"
    :flyer-id="String(route.params.id || apiFlyer?.id || '')"
    :redirect-to="redirectTo"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import CreateFlyer from '@/views/CreateFlyer.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { extractFlyerDetail, normalizeFlyerDetail } from '@/utils/flyerDetail'

useHead({
  title: 'Edit Flyer | NetFlyr',
  meta: [
    { name: 'description', content: 'Edit your NetFlyr flyer details, images, dates, location, visibility, business review, or checkin information to keep local posts accurate, relevant, and useful for nearby users.' },
    { name: 'keywords', content: 'edit flyer, update flyer, NetFlyr flyer editing, manage local post, update flyer images, flyer details, local posting' }
  ]
})

const route = useRoute()
const session = useSessionStore()
const apiFlyer = ref(null)
const isLoading = ref(true)
const loadError = ref('')
let detailRequestId = 0

const redirectTo = computed(() => {
  if (String(route.query.type || '').toLowerCase() === 'checkin') return '/checkins'

  if (typeof route.query.returnTo === 'string' && route.query.returnTo.trim()) {
    return route.query.returnTo
  }

  const back = window.history.state?.back
  return back && !String(back).includes('/edit') ? back : `/flyers/${route.params.id}`
})

const editPageTitle = computed(() => {
  const type = String(apiFlyer.value?.flyer_type || route.query.type || '').toLowerCase()
  if (type === 'checkin') return 'Edit Checkin'
  if (type === 'review') return 'Edit Review'
  return 'Edit Flyer'
})

const fetchFlyerDetail = async () => {
  const flyerId = route.params.id
  if (!flyerId) {
    isLoading.value = false
    loadError.value = 'Flyer details could not be loaded.'
    return
  }
  const requestId = ++detailRequestId
  isLoading.value = true
  loadError.value = ''
  apiFlyer.value = null

  try {
    const checkinState = String(route.query.type || '').toLowerCase() === 'checkin' && typeof window !== 'undefined'
      ? window.history.state?.checkin
      : null
    if (checkinState && typeof checkinState === 'object') {
      if (requestId !== detailRequestId) return
      apiFlyer.value = normalizeFlyerDetail({ ...checkinState, flyer_type: 'checkin' })
      return
    }

    const { data } = await api.detailFlyer({
      user_id: session.userId || session.user?.user_id || '',
      flyer_id: flyerId
    })
    const detail = extractFlyerDetail(data)
    if (requestId !== detailRequestId) return
    if (!detail) throw new Error('Flyer detail was missing from the response.')
    apiFlyer.value = normalizeFlyerDetail({
      ...detail,
      flyer_type: String(route.query.type || '').toLowerCase() === 'checkin'
        ? 'checkin'
        : detail.flyer_type
    })
  } catch (error) {
    if (requestId !== detailRequestId) return
    apiFlyer.value = null
    loadError.value = error?.response?.data?.message || 'Flyer details and images could not be loaded. Please try again.'
  } finally {
    if (requestId === detailRequestId) isLoading.value = false
  }
}

watch(() => route.params.id, fetchFlyerDetail, { immediate: true })
</script>
